
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import axios from "axios";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

// Use the same API URL configuration as your other pages.
// import { API_URL } from "...";

function TeamExpirationReport() {
  const [teamData, setTeamData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchTeamExpirationData = async () => {
      try {
        setLoading(true);
        setError("");

        // Broad date range for initial testing.
        const startDate = "2016-01-01";
        const endDate = "2036-12-31";

        const response = await axios.post(
          "/getUserCoursesByExpirationRange",
          { startDate, endDate }
        );

        setTeamData(response.data);
      } catch (err) {
        console.error(
          "Failed to retrieve team expiration data:",
          err
        );
        setError("Failed to load team expiration data.");
      } finally {
        setLoading(false);
      }
    };

    fetchTeamExpirationData();
  }, []);

  const getTeamStats = (team) => {
    const courses = team.userCourses || [];
    const recipients = team.recipients || [];

    const now = new Date();

    const firstDayOfThisMonth = new Date(
      now.getFullYear(),
      now.getMonth(),
      1
    );

    const firstDayOfLastMonth = new Date(
      now.getFullYear(),
      now.getMonth() - 1,
      1
    );

    const twoMonthsFromNow = new Date(
      now.getFullYear(),
      now.getMonth() + 2,
      now.getDate()
    );

    const expirationCourses = courses.filter(
      (course) => course.trainingStatus === "expiration"
    );

    const incompleteCourses = courses.filter(
      (course) => course.trainingStatus === "incomplete"
    );

    const uniqueMembers = new Set(
      courses
        .map((course) =>
          String(course.userEmail || "").trim().toLowerCase()
        )
        .filter(Boolean)
    );

    const expiredCourses = expirationCourses.filter(
      (course) => new Date(course.expiresOn) < now
    );

    const expiredLastMonth = expirationCourses.filter(
      (course) => {
        const date = new Date(course.expiresOn);

        return (
          date >= firstDayOfLastMonth &&
          date < firstDayOfThisMonth
        );
      }
    );

    const expiringNextTwoMonths = expirationCourses.filter(
      (course) => {
        const date = new Date(course.expiresOn);

        return date >= now && date <= twoMonthsFromNow;
      }
    );

    return {
      totalRecipients: recipients.length,
      totalMembers: uniqueMembers.size,
      totalCourses: courses.length,
      expiredCourses: expiredCourses.length,
      expiredLastMonth: expiredLastMonth.length,
      expiringNextTwoMonths: expiringNextTwoMonths.length,
      incompleteTraining: incompleteCourses.length,
    };
  };

  return (
    <Container>
      <HeaderPadding />
      <HeaderPadding />

      <ExpirationReport>
        <Table
          sx={{ minWidth: 950 }}
          aria-label="team expiration report"
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>
                Team Name
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                Total Admin / Owner
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                Total Members*
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                Total Team Courses*
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                Expired Courses
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                Expired Last Month
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                Next 2 Months
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                Incomplete Training
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8}>
                  Loading team expiration data...
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={8}>{error}</TableCell>
              </TableRow>
            ) : teamData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>
                  No team expiration records found.
                </TableCell>
              </TableRow>
            ) : (
              teamData.map((team) => {
                const stats = getTeamStats(team);

                return (
                  <TableRow key={team.teamId}>
                    <TableCell>
                      {team.teamName || team.teamId}
                    </TableCell>
                    <TableCell>
                      {stats.totalRecipients}
                    </TableCell>
                    <TableCell>
                      {stats.totalMembers}
                    </TableCell>
                    <TableCell>
                      {stats.totalCourses}
                    </TableCell>
                    <TableCell>
                      {stats.expiredCourses}
                    </TableCell>
                    <TableCell>
                      {stats.expiredLastMonth}
                    </TableCell>
                    <TableCell>
                      {stats.expiringNextTwoMonths}
                    </TableCell>
                    <TableCell>
                      {stats.incompleteTraining}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </ExpirationReport>
    </Container>
  );
}

export default TeamExpirationReport;

const Container = styled.div`
  width: 100%;
  min-height: 45rem;
`;

const HeaderPadding = styled.div`
  width: 100%;
  padding-top: 70px;
`;

const ExpirationReport = styled.div`
  width: 85%;
  max-height: 70vh;
  margin: 0 auto;
  display: flex;
  padding: 0px 20px 20px 20px;
  border: 1px solid #55555540;
  background-color: #ededed;
  overflow: auto;
`;
